import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { array, hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import PageHeader from 'tio-ui/components/page-header';
import PageControlBar from 'tio-ui/components/page-control-bar';
import SearchInput from 'tio-ui/components/search-input';
import Select from 'tio-ui/components/select';
import NavTabs from 'tio-ui/components/nav-tabs';
import { PhArrowLeft, PhCode, PhUsers, PhBuildings, PhPlus } from 'ember-phosphor-icons';

// ---------------------------------------------------------------------------
// PageHeader / PageControlBar gallery story
//
// Demonstrates the canonical compositions from
// `.github/instructions/page-header.instructions.md` (issue #8419):
//   1. Title only
//   2. Title + subtitle + icon
//   3. :toolbar single search (1 input → title row)
//   4. :actions primary CTA
//   5. :below <PageControlBar> with :filters + @count + :trailing
//   6. :tabs band example
//   7. Detail-header :below metadata text (the `…/show` shape)
//
// Example/section scaffolding uses the `.app-gallery-*` recipes (tio-ui
// styles.css) and `.tio-*` composition primitives — the reimagined gallery
// surface is held to the semantic-class standard, so demos stay recipe-driven
// rather than reaching for raw utility chains. (The shared story chrome — the
// `<VStack class="w-full min-w-0">` root and `mb-4` back-nav, common to every
// `reimagined/components/*` story — keeps the few layout utilities it needs.)
// ---------------------------------------------------------------------------

const ROLE_OPTIONS = [
  { key: 'all', label: 'All roles' },
  { key: 'admin', label: 'Admin' },
  { key: 'member', label: 'Member' },
];

const SORT_OPTIONS = [
  { key: 'recent', label: 'Most recent' },
  { key: 'name', label: 'Name (A–Z)' },
];

export default class PageHeaderStory extends Component {
  @tracked search = '';
  @tracked role = 'all';
  @tracked sort = 'recent';

  roleOptions = ROLE_OPTIONS;
  sortOptions = SORT_OPTIONS;

  setSearch = (value: string) => {
    this.search = value;
  };

  setRole = (key: string) => {
    this.role = key;
  };

  setSort = (key: string) => {
    this.sort = key;
  };

  clearAll = () => {
    this.search = '';
    this.role = 'all';
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.components"
          class="tio-inline-row-tight tio-fg-brand"
        >
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero
        @title="PageHeader & PageControlBar"
        @subtitle="The two-zone page header pattern. Primary header for title/icon/search/actions; the conditional secondary control bar for filters, tabs, and the count/sort/clear cluster."
        @icon={{PhCode}}
        @gradient="brand"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">Pattern</Chip>
          <Chip @intent="success">Issue #8419</Chip>
        </:actions>
      </PageHero>

      <div class="app-gallery-sections">
        {{! ===== 1. Title only ===== }}
        <div class="app-gallery-example">
          <span class="app-gallery-example__label">Title only</span>
          <span class="app-gallery-example__hint">
            The minimum header — a single semantic h1. No subtitle, no toolbar, no actions, no
            secondary bar.
          </span>
          <div class="app-gallery-stage">
            <PageHeader @title="Dashboard" />
            <div class="app-gallery-stage__body">Route content renders here.</div>
          </div>
        </div>

        {{! ===== 2. Title + subtitle + icon ===== }}
        <div class="app-gallery-example">
          <span class="app-gallery-example__label">Title + subtitle + icon</span>
          <span class="app-gallery-example__hint">
            A Phosphor `@icon` before the title and a one-line `@subtitle` description below it.
          </span>
          <div class="app-gallery-stage">
            <PageHeader
              @title="People"
              @subtitle="Everyone in your organization."
              @icon={{PhUsers}}
            />
            <div class="app-gallery-stage__body">Route content renders here.</div>
          </div>
        </div>

        {{! ===== 3. :toolbar single search ===== }}
        <div class="app-gallery-example">
          <span class="app-gallery-example__label">Inline search (:toolbar)</span>
          <span class="app-gallery-example__hint">
            One search input stays inline in the title row via `:toolbar` — search never leaves the
            title row, even once the secondary bar appears.
          </span>
          <div class="app-gallery-stage">
            <PageHeader @title="People" @icon={{PhUsers}}>
              <:toolbar>
                <SearchInput
                  @placeholder="Search people…"
                  @value={{this.search}}
                  @onChange={{this.setSearch}}
                />
              </:toolbar>
            </PageHeader>
            <div class="app-gallery-stage__body">Route content renders here.</div>
          </div>
        </div>

        {{! ===== 4. :actions primary CTA ===== }}
        <div class="app-gallery-example">
          <span class="app-gallery-example__label">Primary action (:actions)</span>
          <span class="app-gallery-example__hint">
            Primary actions live in `:actions`, right-aligned — never in a separate bar lower on the
            page.
          </span>
          <div class="app-gallery-stage">
            <PageHeader @title="People" @icon={{PhUsers}}>
              <:toolbar>
                <SearchInput
                  @placeholder="Search people…"
                  @value={{this.search}}
                  @onChange={{this.setSearch}}
                />
              </:toolbar>
              <:actions>
                <Button @intent="primary" @icon={{PhPlus}}>Create employee</Button>
              </:actions>
            </PageHeader>
            <div class="app-gallery-stage__body">Route content renders here.</div>
          </div>
        </div>

        {{! ===== 5. :below PageControlBar — filters + count + trailing ===== }}
        <div class="app-gallery-example">
          <span class="app-gallery-example__label">
            Secondary control bar (:below → PageControlBar)
          </span>
          <span class="app-gallery-example__hint">
            Three+ inputs spill the filter cluster into a PageControlBar in `:below`. The
            standardized `@count` / sort / Clear-all cluster sits on the right. Search stays inline
            in `:toolbar`.
          </span>
          <div class="app-gallery-stage">
            <PageHeader @title="People" @icon={{PhUsers}}>
              <:toolbar>
                <SearchInput
                  @placeholder="Search people…"
                  @value={{this.search}}
                  @onChange={{this.setSearch}}
                />
              </:toolbar>
              <:actions>
                <Button @intent="primary" @icon={{PhPlus}}>Create employee</Button>
              </:actions>
              <:below>
                <PageControlBar @count={{1204}} @countLabel="people">
                  <:filters>
                    <Select
                      @label="Role"
                      @items={{this.roleOptions}}
                      @selected={{this.role}}
                      @onChange={{this.setRole}}
                    />
                    <Select
                      @label="Sort"
                      @items={{this.sortOptions}}
                      @selected={{this.sort}}
                      @onChange={{this.setSort}}
                    />
                  </:filters>
                  <:trailing>
                    <Button @appearance="minimal" @intent="danger" {{on "click" this.clearAll}}>
                      Clear all
                    </Button>
                  </:trailing>
                </PageControlBar>
              </:below>
            </PageHeader>
            <div class="app-gallery-stage__body">Route content renders here.</div>
          </div>
        </div>

        {{! ===== 6. :tabs band ===== }}
        <div class="app-gallery-example">
          <span class="app-gallery-example__label">Tabs band (:tabs)</span>
          <span class="app-gallery-example__hint">
            Tabs always live in the secondary bar, never in the title row. A tabbed page has a
            PageControlBar even with zero filters. (These tabs link to sibling gallery routes so
            they are real, route-based tabs.)
          </span>
          <div class="app-gallery-stage">
            <PageHeader @title="Components" @icon={{PhCode}}>
              <:below>
                <PageControlBar>
                  <:tabs>
                    <NavTabs
                      @tabs={{array
                        (hash label="Forms" route="authenticated.reimagined.components.forms")
                        (hash label="Buttons" route="authenticated.reimagined.components.buttons")
                        (hash label="Cards" route="authenticated.reimagined.components.cards")
                        (hash
                          label="Navigation" route="authenticated.reimagined.components.navigation"
                        )
                      }}
                    />
                  </:tabs>
                </PageControlBar>
              </:below>
            </PageHeader>
            <div class="app-gallery-stage__body">Route content renders here.</div>
          </div>
        </div>

        {{! ===== 7. Detail header — :below metadata text ===== }}
        <div class="app-gallery-example">
          <span class="app-gallery-example__label">Detail header (metadata in :below)</span>
          <span class="app-gallery-example__hint">
            On a `…/show` detail route the `:below` slot may hold read-only secondary metadata text
            (not a control cluster). Interactive controls stay in `:actions`; static metadata is
            sanctioned here without a PageControlBar.
          </span>
          <div class="app-gallery-stage">
            <PageHeader @title="Acme Corporation" @icon={{PhBuildings}}>
              <:actions>
                <Chip @intent="success">Active</Chip>
                <Chip @intent="primary">Partner</Chip>
              </:actions>
              <:below>
                <div class="tio-cluster-tight tio-fg-muted">
                  <span>Partner since Jan 2021</span>
                  <span aria-hidden="true">·</span>
                  <span>SSO via Okta</span>
                  <span aria-hidden="true">·</span>
                  <span>acme.tuition.io</span>
                </div>
              </:below>
            </PageHeader>
            <div class="app-gallery-stage__body">Route content renders here.</div>
          </div>
        </div>
      </div>

      {{! ===== Rules summary ===== }}
      <div class="app-card p-6">
        <Heading @level={{3}} class="mb-2">When does the secondary bar appear?</Heading>
        <Text @variant="muted" class="mb-4">
          Count the interactive inputs the page needs at the top. Two is the ceiling for the title
          row; three or more spills the filter cluster to the bar. Tabs always live in the bar.
        </Text>
        <div class="tio-cluster">
          <Chip @intent="success" @size="sm">0–2 inputs → title row, no bar</Chip>
          <Chip @intent="primary" @size="sm">3+ inputs → filters in the bar</Chip>
          <Chip @intent="primary" @size="sm">Any tabs → bar (tabs band)</Chip>
          <Chip @intent="success" @size="sm">Search always stays inline</Chip>
        </div>
      </div>
    </VStack>
  </template>
}
